import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { GENRE_HIERARCHY } from '@/constants/genres';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const GenreSelector = ({ selectedGenres = [], onChange, className = "", disabled = false }) => {
  const [expandedParents, setExpandedParents] = useState({});
  const [isSuggestionDialogOpen, setIsSuggestionDialogOpen] = useState(false);
  const [genreName, setGenreName] = useState('');
  const [genreDescription, setGenreDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleParent = (parent) => {
    setExpandedParents(prev => ({
      ...prev,
      [parent]: !prev[parent]
    }));
  };

  const handleSubgenreToggle = (subgenre) => {
    const newSelection = selectedGenres.includes(subgenre)
      ? selectedGenres.filter(g => g !== subgenre)
      : [...selectedGenres, subgenre];
    onChange(newSelection);
  };

  const getSelectedCount = (parent) => {
    const subgenres = GENRE_HIERARCHY[parent];
    return subgenres.filter(sg => selectedGenres.includes(sg)).length;
  };

  const handleSuggestionSubmit = async (e) => {
    e.preventDefault();
    
    if (!genreName.trim()) {
      toast.error('Please enter a genre name');
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('You must be logged in to suggest a genre');
        setIsSubmitting(false);
        return;
      }

      const { error } = await supabase
        .from('genre_suggestions')
        .insert({
          user_id: session.user.id,
          genre_name: genreName.trim(),
          description: genreDescription.trim() || null,
          status: 'pending'
        });

      if (error) throw error;

      toast.success("Genre suggestion submitted! We'll review it soon.");
      setGenreName('');
      setGenreDescription('');
      setIsSuggestionDialogOpen(false);
    } catch (error) {
      console.error('Error submitting genre suggestion:', error);
      const message = typeof error?.message === 'string' ? error.message.toLowerCase() : '';
      if (message.includes('duplicate') || message.includes('already exists')) {
        toast.error('This genre has already been suggested');
      } else {
        toast.error('Failed to submit genre suggestion');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {Object.entries(GENRE_HIERARCHY).map(([parent, subgenres]) => {
        const isExpanded = expandedParents[parent];
        const selectedCount = getSelectedCount(parent);

        return (
          <div key={parent} className="neuro-flat rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => toggleParent(parent)}
              disabled={disabled}
              className="w-full flex items-center justify-between p-3 text-left disabled:opacity-50"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[#d0d0d0]">{parent}</span>
                {selectedCount > 0 && (
                  <span className="neuro-pressed px-2 py-0.5 rounded-full text-xs text-[#a0a0a0]">
                    {selectedCount}
                  </span>
                )}
              </div>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-[#808080]" />
              ) : (
                <ChevronDown className="w-4 h-4 text-[#808080]" />
              )}
            </button>

            {isExpanded && (
              <div className="px-3 pb-3 grid grid-cols-2 gap-2">
                {subgenres.map((subgenre) => (
                  <div
                    key={subgenre}
                    className="flex items-center gap-2"
                  >
                    <Checkbox
                      id={`genre-${subgenre}`}
                      checked={selectedGenres.includes(subgenre)}
                      onCheckedChange={() => handleSubgenreToggle(subgenre)}
                      disabled={disabled}
                      className="border-[#404040] data-[state=checked]:bg-[#505050] data-[state=checked]:border-[#606060]"
                    />
                    <label
                      htmlFor={`genre-${subgenre}`}
                      className="text-xs text-[#a0a0a0] cursor-pointer"
                    >
                      {subgenre}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => setIsSuggestionDialogOpen(true)}
        className="w-full p-3 text-center text-xs text-[#808080] hover:text-[#a0a0a0] transition-colors"
      >
        Don't see your genre? Suggest one →
      </button>

      <Dialog open={isSuggestionDialogOpen} onOpenChange={setIsSuggestionDialogOpen}>
        <DialogContent className="neuro-base border-[#2a2a2a]">
          <DialogHeader>
            <DialogTitle className="text-[#d0d0d0]">Suggest a Genre</DialogTitle>
            <DialogDescription className="text-[#808080]">
              Help us expand our genre list by suggesting a new genre.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSuggestionSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-[#a0a0a0] mb-1 block">Genre Name *</label>
              <Input
                value={genreName}
                onChange={(e) => setGenreName(e.target.value)}
                placeholder="e.g., Phonk, Witch House"
                className="neuro-pressed border-none text-[#d0d0d0]"
                required
              />
            </div>

            <div>
              <label className="text-xs text-[#a0a0a0] mb-1 block">Description (optional)</label>
              <Textarea
                value={genreDescription}
                onChange={(e) => setGenreDescription(e.target.value)}
                placeholder="Brief description of the genre..."
                className="neuro-pressed border-none text-[#d0d0d0] min-h-[80px]"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsSuggestionDialogOpen(false)}
                className="text-[#808080]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !genreName.trim()}
                className="neuro-base active:neuro-pressed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Suggestion'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GenreSelector;